import { useEffect, useState } from 'react';

import { OralExamConductPhase } from '@/common/api/models/oralExamConductPhase';
import { OralExamDriver } from '@/common/api/models/oralExamDriver';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import type { StudentExamShellPhase } from '@/student/module/utils/deriveStudentRuntimeJourney';

/** Mid-exam silence escalates to reconnecting after this grace (1:N AI-death window). */
export const EXAMINER_AGENT_ESCALATE_MS = 30_000;

/**
 * Cold start (waiting for first examiner_ready after dispatch) needs a longer
 * grace — LiveKit Cloud agent claim + Nexus FLARE_API_BASE handshake often
 * exceeds 5s. Escalating at 5s during `waiting_for_examiner` falsely showed
 * "Examiner unavailable" while a healthy cold join was still in flight.
 */
export const EXAMINER_AGENT_COLD_START_ESCALATE_MS = 30_000;

export type ExaminerAgentWatchdogInput = {
  phase: StudentExamShellPhase;
  studentDoneAt?: string | null;
  currentQuestionIndex: number;
  currentQuestionId?: string | null;
  /** When true (agent audio speaking), clear the watch. */
  voiceSpeaking?: boolean;
  /** When true (LiveKit voice-assistant agent connected), clear the watch. */
  agentPresent?: boolean;
  /** Orb thinking/listening — examiner is working, not silent. */
  examinerWorking?: boolean;
  /** Help/drop-in pause — expected hold, never watch. */
  isPaused?: boolean;
  /**
   * Appear (`here_with_you`) — lecturer is visible; do not steal the question
   * with Examiner unavailable. Scripted / dummy agents have no voice-assistant
   * presence, so mid-exam silence would otherwise fire at 5s.
   */
  supervisorVisible?: boolean;
  escalateMs?: number;
  nowMs?: number;
};

export type ExaminerAgentWatchdogResult = {
  reconnecting: boolean;
  unavailable: boolean;
};

export type ExaminerAgentWatchContext = {
  isPaused?: boolean;
  supervisorVisible?: boolean;
};

/**
 * Phases where the watchdog tracks examiner silence. Cold start uses the 30s SLA
 * (hard stop via {@link shouldExaminerUnavailableHardStop}); expected holds
 * (Done, between questions, pause) are never watched. Callers must not map
 * `reconnecting` to the orb during cold-start grace — caption stays honest.
 */
export function isExaminerAgentWatchPhase(
  phase: StudentExamShellPhase,
  studentDoneAt?: string | null,
  ctx?: ExaminerAgentWatchContext,
): boolean {
  if (ctx?.isPaused) return false;
  if (ctx?.supervisorVisible) return false;
  if (phase === 'ready_to_start' || phase === 'between_questions') {
    return false;
  }
  if (phase === 'question_active' && studentDoneAt) {
    return false;
  }
  return phase === 'starting'
    || phase === 'waiting_for_examiner'
    || phase === 'question_active';
}

/** Cold-start handshake phases where unavailable escalates to a full-screen hard stop. */
export function isExaminerAgentColdStartPhase(phase: StudentExamShellPhase): boolean {
  return phase === 'starting' || phase === 'waiting_for_examiner';
}

/**
 * After the watchdog fires unavailable, cold-start phases hard-stop the student;
 * mid-exam silence keeps the in-shell help/abort surface.
 */
export function shouldExaminerUnavailableHardStop(
  phase: StudentExamShellPhase,
  unavailable: boolean,
): boolean {
  return unavailable && isExaminerAgentColdStartPhase(phase);
}

/** Backend conduct phases that share the student cold-start grace window. */
export function isExaminerColdStartConductPhase(
  conductPhase?: OralExamConductPhase | null,
): boolean {
  return conductPhase === OralExamConductPhase.waiting_for_examiner
    || conductPhase === OralExamConductPhase.starting_exam;
}

/**
 * Lecturer cohort card: mirror the student cold-start SLA using server `startedAt`
 * as the handshake anchor (poll-side approximation of the student watchdog).
 */
export function shouldShowLecturerExaminerUnavailable(
  runState: Pick<
  OralExamRunStateResponse,
  'driver' | 'conductPhase' | 'examinerReadyAt' | 'startedAt' | 'operationalFault'
  >,
  nowMs: number = Date.now(),
): boolean {
  if (runState.operationalFault) return false;
  if (runState.driver !== OralExamDriver.examiner_agent) return false;
  if (runState.examinerReadyAt) return false;
  if (!isExaminerColdStartConductPhase(runState.conductPhase)) return false;
  const anchorMs = runState.startedAt ? Date.parse(runState.startedAt) : Number.NaN;
  if (Number.isNaN(anchorMs)) return false;
  return nowMs - anchorMs >= EXAMINER_AGENT_COLD_START_ESCALATE_MS;
}

/** Cold-start phases get a longer escalate; mid-exam silence stays aggressive. */
export function resolveExaminerAgentEscalateMs(phase: StudentExamShellPhase): number {
  if (phase === 'starting' || phase === 'waiting_for_examiner') {
    return EXAMINER_AGENT_COLD_START_ESCALATE_MS;
  }
  return EXAMINER_AGENT_ESCALATE_MS;
}

export function examinerAgentProgressKey(input: {
  phase: StudentExamShellPhase;
  studentDoneAt?: string | null;
  currentQuestionIndex: number;
  currentQuestionId?: string | null;
}): string {
  return [
    input.phase,
    input.studentDoneAt ?? '',
    String(input.currentQuestionIndex),
    input.currentQuestionId ?? '',
  ].join('|');
}

/**
 * Pure derivation: given when the current stuck episode began, compute soft/hard
 * unavailable flags. `stuckSinceMs === null` means not watching.
 *
 * Soft reconnect only after grace and only when the agent is not speaking/present.
 * Reconnecting copy is LiveKit transport only — callers must not map `reconnecting`
 * to the examiner orb on expected holds.
 */
export function deriveExaminerAgentWatchdog(input: {
  watching: boolean;
  voiceSpeaking: boolean;
  agentPresent: boolean;
  /** Thinking/listening after a read — not silence. Hook must store null, not the prior clock. */
  examinerWorking?: boolean;
  stuckSinceMs: number | null;
  nowMs: number;
  escalateMs?: number;
  /** When true (cold-start handshake), LiveKit presence/speech do not clear the watch. */
  coldStart?: boolean;
}): ExaminerAgentWatchdogResult & { stuckSinceMs: number | null } {
  if (!input.watching) {
    return { reconnecting: false, unavailable: false, stuckSinceMs: null };
  }
  if (
    !input.coldStart
    && (input.voiceSpeaking || input.agentPresent || input.examinerWorking)
  ) {
    return { reconnecting: false, unavailable: false, stuckSinceMs: null };
  }
  const since = input.stuckSinceMs ?? input.nowMs;
  const escalateMs = input.escalateMs ?? EXAMINER_AGENT_ESCALATE_MS;
  const elapsed = input.nowMs - since;

  if (elapsed < escalateMs) {
    return { reconnecting: false, unavailable: false, stuckSinceMs: since };
  }

  return {
    stuckSinceMs: since,
    reconnecting: true,
    unavailable: true,
  };
}

/**
 * Student-side examiner silence / non-advance watchdog.
 * Only active mid-question (not Done, not paused). Expected waits never surface
 * Reconnecting on the orb — transport overlay is LiveKit-only.
 *
 * Clock ownership: speaking / present / thinking must persist `stuckSinceMs: null`.
 * Do not write derive's `stuckSinceMs ?? nowMs` back into state — that keeps the
 * question-start clock across a long Q1 read, so the shell goes unavailable the
 * instant TTS ends. Seed silence with `Date.now()` (fake-timer aware), then tick
 * `nowMs` while watching so a later silence can escalate.
 */
export function useExaminerAgentWatchdog(
  input: ExaminerAgentWatchdogInput,
): ExaminerAgentWatchdogResult {
  const escalateMs = input.escalateMs ?? resolveExaminerAgentEscalateMs(input.phase);
  const watching = isExaminerAgentWatchPhase(input.phase, input.studentDoneAt, {
    isPaused: input.isPaused,
    supervisorVisible: input.supervisorVisible,
  });
  const coldStart = isExaminerAgentColdStartPhase(input.phase);
  const progressKey = examinerAgentProgressKey(input);
  const healthy = !coldStart && Boolean(
    input.voiceSpeaking || input.agentPresent || input.examinerWorking,
  );

  const [stuckSinceMs, setStuckSinceMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => input.nowMs ?? Date.now());

  useEffect(() => {
    if (!watching || healthy) {
      setStuckSinceMs(null);
      return;
    }
    setStuckSinceMs(Date.now());
  }, [watching, progressKey, healthy]);

  useEffect(() => {
    if (!watching) return undefined;
    const id = setInterval(() => {
      setNowMs(Date.now());
    }, 250);
    return () => clearInterval(id);
  }, [watching]);

  const derived = deriveExaminerAgentWatchdog({
    watching,
    voiceSpeaking: Boolean(input.voiceSpeaking),
    agentPresent: Boolean(input.agentPresent),
    examinerWorking: Boolean(input.examinerWorking),
    stuckSinceMs,
    nowMs: input.nowMs ?? nowMs,
    escalateMs,
    coldStart,
  });

  return {
    reconnecting: derived.reconnecting,
    unavailable: derived.unavailable,
  };
}
